/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  PRODUCT_STOCK_STATUS,
  type ProductStatusBadgeInfo as ProductCardStatusBadgeInfo,
  type ProductStockInfo as ProductCardStockInfo,
  type ProductStockStatus as ProductCardStockStatus,
} from "@farmlands/commerce";

import { CartDeliveryMode } from "../../cart/storage/cart.types";
import {
  StockAvailabilityCodes,
  StockAvailabilityStatusType,
} from "../components/StockAvailability/StockAvailabilityStatus";
import { LifeCycleState, StockAvailabilityType } from "../storage";

/** Inventory location prefix for home-branch delivery stock. */
export const DELIVERY_LOCATION_PREFIX = "265";

export interface FarmerAccountContext {
  homeBranchId?: string;
  hasTfoAccess?: boolean;
}

export const getProductStatus = (
  lifeCycleState: string | null | undefined,
  basePriceExcGst: number | undefined
) => {
  const noPriceAvailable: boolean = basePriceExcGst === undefined || basePriceExcGst <= 0.0;
  const isUnavailable: boolean = !lifeCycleState || noPriceAvailable;
  const isDiscontinued: boolean =
    lifeCycleState === LifeCycleState.OBSOLETE || lifeCycleState === LifeCycleState.DISCONTINUED;
  const isEndOfLife: boolean = lifeCycleState === LifeCycleState.END_OF_LIFE;

  return { noPriceAvailable, isUnavailable, isDiscontinued, isEndOfLife };
};

const stockCodeToStatus: Record<StockAvailabilityCodes, ProductCardStockStatus> = {
  [StockAvailabilityCodes.IN_STOCK]: PRODUCT_STOCK_STATUS.IN_STOCK,
  [StockAvailabilityCodes.LOW_STOCK]: PRODUCT_STOCK_STATUS.LOW_STOCK,
  [StockAvailabilityCodes.OUT_OF_STOCK]: PRODUCT_STOCK_STATUS.OUT_OF_STOCK,
  [StockAvailabilityCodes.URGENT_UNAVAILABLE]: PRODUCT_STOCK_STATUS.URGENT_UNAVAILABLE,
  [StockAvailabilityCodes.SEE_MORE_OPTIONS]: PRODUCT_STOCK_STATUS.MORE_OPTIONS,
};

export const mapInventoryStatusToStockInfo = (
  status: StockAvailabilityStatusType,
  isBackOrderAllowed: boolean | undefined,
  isTfo: boolean
): ProductCardStockInfo | undefined => {
  const code: StockAvailabilityCodes | undefined = status.code as
    StockAvailabilityCodes | undefined;

  if (!code && !status.label) {
    if (isTfo) {
      return {
        status: PRODUCT_STOCK_STATUS.OUT_OF_STOCK,
        label: "Out of stock",
        backordersAvailable: !!isBackOrderAllowed,
      };
    }

    return undefined;
  }

  if (!code || !status.label) {
    return undefined;
  }

  let label: string | undefined = status.label;
  let count: number | undefined;

  if (typeof status.quantity === "number") {
    count = status.quantity;
    if (status.quantity > 0) {
      label = undefined;
    } else if (!isTfo) {
      label = "Out of stock";
      count = undefined;
    } else {
      label = `${status.quantity.toFixed(0)} in stock`;
    }
  }

  return {
    status: stockCodeToStatus[code] ?? PRODUCT_STOCK_STATUS.OUT_OF_STOCK,
    count,
    label,
    backordersAvailable: !!isBackOrderAllowed && code === StockAvailabilityCodes.OUT_OF_STOCK,
  };
};

export interface ActiveStockAvailabilityParams {
  stockAvailabilities: StockAvailabilityType[];
  productId: number;
  account: FarmerAccountContext;
  cartDeliveryMode?: CartDeliveryMode;
  pickupStore?: { inventoryLocationId?: string; orgUnitName?: string };
}

export const getActiveStockAvailability = (
  params: ActiveStockAvailabilityParams
): StockAvailabilityType | undefined => {
  const { stockAvailabilities, productId, account, cartDeliveryMode, pickupStore } = params;

  if (cartDeliveryMode === CartDeliveryMode.PICKUP) {
    const locationId: string | undefined = pickupStore?.inventoryLocationId ?? account.homeBranchId;
    return stockAvailabilities.find(
      (entry: StockAvailabilityType) =>
        entry.inventLocationId === locationId && entry.productId === productId
    );
  }

  return stockAvailabilities.find(
    (entry: StockAvailabilityType) =>
      entry.inventLocationId === `${DELIVERY_LOCATION_PREFIX}-${account.homeBranchId}` &&
      entry.productId === productId
  );
};

export const availabilityForStore = (
  stockAvailabilities: StockAvailabilityType[],
  productId: number,
  storeId: string
): StockAvailabilityType | undefined =>
  stockAvailabilities.find(
    (entry: StockAvailabilityType) =>
      entry.inventLocationId === storeId ||
      (entry.inventLocationId.endsWith(storeId) && entry.productId === productId)
  );

export const getLifecycleStatusBadge = (
  lifeCycleState: string | null | undefined,
  basePriceExcGst: number | undefined,
  isTfo: boolean
): ProductCardStatusBadgeInfo | undefined => {
  const { noPriceAvailable, isUnavailable, isDiscontinued, isEndOfLife } = getProductStatus(
    lifeCycleState,
    basePriceExcGst
  );

  let label = "";
  let subtitle: string | undefined;

  if (noPriceAvailable || isUnavailable) {
    label = "Unavailable";
  }

  if (isDiscontinued) {
    label = "Discontinued";
  }

  if (isEndOfLife) {
    label = "End of product line";
  }

  if (isTfo) {
    if (lifeCycleState === LifeCycleState.NOT_STOCKED) {
      label = "Active not stocked";
    }

    if (lifeCycleState === LifeCycleState.TEMPORARILY_UNAVAILABLE) {
      label = "Temporarily unavailable";
    }
  } else if (lifeCycleState === LifeCycleState.TEMPORARILY_UNAVAILABLE) {
    label = "Temporarily unavailable";
    subtitle = "We will contact you for fulfilment options.";
  }

  return label ? { label, subtitle } : undefined;
};
